/**
 * Staff module home: portal-only users (myAttendance.view without employees.view)
 * should land on My dashboard, not the office Staff workspace.
 * Payroll / settings / holidays access keeps them on the hub instead.
 */
export function shouldRedirectStaffHomeToMyAttendance(
  canManageStaff: boolean,
  canViewMyAttendance: boolean,
  canAccessPayrollSurfaces = false
): boolean {
  return !canManageStaff && canViewMyAttendance && !canAccessPayrollSurfaces;
}

/** Account menu: offer My dashboard only for dual-hat office + personal users. */
export function shouldShowPersonalDashboardUserMenuItem(
  canViewMyAttendance: boolean,
  canManageStaff: boolean
): boolean {
  // Portal-only: Staff home already redirects to My dashboard — no duplicate menu item.
  return Boolean(canViewMyAttendance && canManageStaff);
}

/** Staff hub: My dashboard card when not duplicated by user menu (dual-hat) or redirect. */
export function shouldShowMyDashboardHubCard(
  canViewMyAttendance: boolean,
  canManageStaff: boolean
): boolean {
  return (
    canViewMyAttendance &&
    !shouldShowPersonalDashboardUserMenuItem(canViewMyAttendance, canManageStaff)
  );
}

/** Staff hub: Leave calendar is available to portal and office staff. */
export function canAccessLeaveCalendar(
  canManageStaff: boolean,
  canViewMyAttendance: boolean
): boolean {
  return canManageStaff || canViewMyAttendance;
}

/**
 * Cross-module target for My dashboard (pathname `/staff/` + hash `/myAttendance`).
 * Use with {@link navigateToBaseRoute} — a hash-only `/myAttendance` link drops the staff module.
 */
export function personalDashboardNavigation(): {
  baseRoute: string;
  hashPaths: string[];
} {
  return { baseRoute: "staff", hashPaths: ["myAttendance"] };
}

export type StaffPayrollHubCard = {
  title: string;
  description: string;
  to: string;
};

/**
 * Hub cards for payroll / setup surfaces.
 * Order: Staff settings → Office holidays → Payroll (configure before running payroll).
 */
export function getStaffPayrollHubCards(flags: {
  canViewPayrollPeriods: boolean;
  canViewStaffSettings: boolean;
  canViewOfficeHolidays: boolean;
}): StaffPayrollHubCard[] {
  const cards: StaffPayrollHubCard[] = [];
  if (flags.canViewStaffSettings) {
    cards.push({
      title: "Staff settings",
      description: "Day basis, professional tax, and leave policy",
      to: "/staffSettings",
    });
  }
  if (flags.canViewOfficeHolidays) {
    cards.push({
      title: "Office holidays",
      description: "Manage the Jamaat holiday calendar",
      to: "/officeHolidays",
    });
  }
  if (flags.canViewPayrollPeriods) {
    cards.push({
      title: "Payroll",
      description: "Monthly payroll runs and payslips",
      to: "/payrollPeriods",
    });
  }
  return cards;
}

/** True when the user can open any payroll setup / run surface on the Staff hub. */
export function canAccessStaffPayrollSurfaces(flags: {
  canViewPayrollPeriods: boolean;
  canViewStaffSettings: boolean;
  canViewOfficeHolidays: boolean;
}): boolean {
  return flags.canViewPayrollPeriods || flags.canViewStaffSettings || flags.canViewOfficeHolidays;
}
